import { ChevronLeftIcon, MessageCircleIcon, UsersIcon } from "lucide-react"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import { Link, Navigate, NavLink, useMatch, useParams } from "react-router-dom"
import { useIsMobile } from "@/hooks/use-mobile"
import { FeatureOff } from "@/components/FeatureOff"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button, buttonVariants } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Skeleton } from "@/components/ui/skeleton"
import { useCan } from "@/features/auth/auth-context"
import { DirectThread } from "@/features/messages/DirectThread"
import { GroupRoom } from "@/features/messages/GroupRoom"
import { initialsOf } from "@/features/messages/message-time"
import {
  directConversations,
  findConversation,
  useDirectConversations,
} from "@/features/messages/use-direct-messages"
import { isModuleOff, useMessagesUnread } from "@/features/messages/use-messages"
import { ApiError } from "@/lib/api-error"
import type { DirectConversationSummary } from "@/lib/endpoints"
import { cn } from "@/lib/utils"
import { routes } from "@/routes/paths"

/**
 * THE MESSENGER — one door, one list, two kinds of row (2026-08-19).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHAT THE OWNER ASKED FOR, AND WHAT IT REPLACED.
 *
 *     Dad
 *     Accountant
 *     ─────────────
 *     Our group chat
 *
 * "so for eg if I've logged in I'll see my dad my accountant and our group
 * chat" — people first, the group as a row BELOW them. Open any row and the
 * conversation appears in the same pane, drawn by the same components.
 *
 * Before this there were TWO screens. `/messages` WAS the shared room, and the
 * private conversations lived at `/messages/direct` behind a button in the
 * room's own header. So the room was the front door and every person was behind
 * it: writing to the accountant took two navigations, and a screen called
 * "Messages" was in fact one particular conversation. Those two paths are GONE —
 * not aliased, not redirected.
 *
 * THE GROUP CHAT IS NOT DELETED. It is `/messages/group`, it keeps its own
 * capability, its own read model and its own route; what it stopped being is a
 * separate PLACE.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * THE LIST IS BESIDE THE PANE, NOT IN FRONT OF IT. Three rows means three rows;
 * a screen you must leave to switch conversation would put a navigation step
 * between "the CA is waiting" and answering her. On a phone the two stack, and
 * arriving with nothing chosen shows the list with the pane's place explained
 * rather than blank.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE TWO CAPABILITIES DECIDE WHAT THE LIST CONTAINS, AND THIS IS WHERE
 * `message.direct` FINALLY EARNS ITS KEEP (§2.1).
 *
 * Its justification in `permissions.ts` is that a business may want an outside
 * professional "confined to the room, where everything said is on the record in
 * front of everyone, and no side channel exists". Until now that was an argument
 * about a link and a route. Now it is the shape of the screen:
 *
 *   `message.view` WITHOUT `message.direct`  → ONE row: the group. No people, no
 *                                              request for them, no separator.
 *   `message.direct` WITHOUT `message.view`  → the people, and no group row.
 *   NEITHER                                  → no messaging surface at all. The
 *                                              route guard sends them Home; if
 *                                              they get here anyway they read a
 *                                              sentence, never a blank pane.
 *
 * EACH HALF IS ALSO GATED ON ITS OWN REQUEST. A reader without `message.direct`
 * never polls `GET /messages/direct`, which would 403 them twice a minute and
 * write every one of those denials to `audit_log` (§2.1). Same for the room's
 * count.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * §18.2 — THE 404 IS THE FLAG, AND IT IS READ ONCE, HERE. There is no `useFlag`
 * on the client (`GET /flags` needs `settings.write`, which the OWNER does not
 * hold), so a switched-off module is read off the status code. It is decided for
 * the WHOLE surface rather than inside a pane, because a disabled feature must
 * read as "not switched on" rather than as an empty conversation beside a list of
 * people that is equally switched off.
 */
export function MessengerScreen(): React.JSX.Element {
  const { t } = useTranslation()

  /*
   * BOTH CAPABILITIES, READ THROUGH `useCan` — the same map `requireCap` reads on
   * the server (§2.1), so the list and the API cannot disagree about who gets a
   * row and who gets a 403.
   */
  const canView = useCan("message.view")
  const canDirect = useCan("message.direct")

  const { userId } = useParams<{ userId: string }>()
  /*
   * `group` IS A LITERAL SEGMENT, so "am I on the room?" is a route question and
   * is asked of the router rather than inferred from the absence of a peer. A
   * screen that guessed would open the room for `/messages/anything`.
   */
  const onGroup = useMatch(routes.messagesGroup) !== null

  /*
   * THE PEER COMES OFF THE URL, AND A URL IS A STRING SOMEBODY CAN TYPE. See
   * {@link parsePeerId}: only a string of digits naming a positive integer is a
   * person, so `/messages/direct` — the path this feature used to live at — is
   * not a conversation, and neither is `/messages/abc`.
   */
  const peerUserId = parsePeerId(userId)

  const conversations = useDirectConversations(canDirect)
  const room = useMessagesUnread(canView)
  const isMobile = useIsMobile()

  /**
   * Is a conversation open — and open to THIS reader?
   *
   * The capability is part of the question, not a separate check: `/messages/7`
   * held by somebody without `message.direct` opens nothing, so the list must
   * stay on screen rather than being hidden for a pane that never appears.
   */
  const showPane = (onGroup && canView) || (peerUserId !== null && canDirect)

  /*
   * SWITCHED OFF IS DECIDED FROM WHICHEVER HALF THIS READER ACTUALLY ASKED FOR.
   * Both routes sit behind the one `messages` flag, so either 404 answers the
   * question; a reader holding only one capability has only one of them to read.
   */
  const moduleOff =
    (canDirect && isModuleOff(conversations.error)) || (canView && room.moduleOff)

  /*
   * A SEGMENT THAT IS NEITHER A PERSON NOR "group" IS NOT A ROUTE. It goes Home,
   * which is exactly what the catch-all does with every other unrecognised path
   * — and it is what makes the deleted `/messages/direct` genuinely gone rather
   * than quietly landing on the list. Placed after every hook, never before one.
   */
  if (userId !== undefined && peerUserId === null) return <Navigate to={routes.home} replace />

  /*
   * NO CAPABILITY AT ALL — a state the route guard normally prevents, kept here
   * because a component that renders nothing is indistinguishable from one that
   * broke. A sentence, never a blank pane.
   */
  if (!canView && !canDirect) {
    return (
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <Card className="flex items-center justify-center p-6" data-testid="no-messaging">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <MessageCircleIcon />
              </EmptyMedia>
              <EmptyTitle>{t("chat.noneTitle")}</EmptyTitle>
              <EmptyDescription>{t("chat.noneHelp")}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        </Card>
      </div>
    )
  }

  if (moduleOff) {
    /*
     * ONE COMPONENT AND ONE PAIR OF STRINGS FOR EVERY FLAGGED MODULE —
     * `components/FeatureOff.tsx`. THE MODULE NAME COMES FROM THE SCREEN'S OWN
     * TITLE rather than a nav label, because the messenger has no sidebar row:
     * you arrive because the badge told you to (see `routes.messages`).
     */
    return (
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <FeatureOff module={t("chat.title")} icon={<MessageCircleIcon />} />
      </div>
    )
  }

  const people = canDirect ? directConversations(conversations.data) : []
  const selected = findConversation(conversations.data, peerUserId)

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">{t("chat.title")}</h1>
        <p className="text-muted-foreground text-sm">{t("chat.subtitle")}</p>
      </header>

      {/*
        * A REAL FAILURE IS A FAILURE, WITH A WAY TO RETRY — and only for the half
        * that failed. A 404 never reaches here; it was handled above as the flag.
        */}
      {canDirect && conversations.isError && !isModuleOff(conversations.error) ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {conversations.error instanceof ApiError
                ? conversations.error.message
                : t("err.somethingWentWrong")}
            </span>
            <Button
              variant="outline"
              onClick={() => {
                void conversations.refetch()
              }}
            >
              {t("btn.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {/*
        ═══════════════════════════════════════════════════════════════════════
        MASTER AND DETAIL ON A PHONE, SIDE BY SIDE ON A DESKTOP.

        The grid used to be the ONLY responsive rule here, and below `md` it
        collapsed to one column with BOTH children still mounted: the list stayed
        where it was and the conversation appeared underneath it. On the owner's
        phone — §11, his primary device — tapping "Accountant" left him looking at
        the same list, with the transcript and the box he came to type in below
        the fold. On an empty conversation nothing scrolled at all, so the tap
        read as having done nothing.

        `useIsMobile` rather than a `hidden md:block` class, deliberately: the
        DOM must genuinely differ, or the fix is invisible to jsdom and the test
        below could not tell a working phone from a broken one. It is the hook
        NotificationBell already uses.
        ═══════════════════════════════════════════════════════════════════════
      */}
      <div className="grid gap-4 md:grid-cols-[minmax(0,18rem)_minmax(0,1fr)] md:gap-6">
        {isMobile && showPane ? null : (
          <ConversationList
            people={people}
            showPeople={canDirect}
            showGroup={canView}
            peopleLoading={canDirect && conversations.isPending}
            peopleLoaded={conversations.data !== undefined}
            groupUnread={room.unread}
            selectedPeerUserId={peerUserId}
          />
        )}

        {/*
          THE WAY BACK, and it only exists on the phone because only there is
          the list gone. Without it the sole route out of a conversation is the
          browser's own back button, which is not a control the owner should have
          to know about.
        */}
        {isMobile && showPane ? (
          <Link
            to={routes.messages}
            /* A PLAIN `Link` WEARING `buttonVariants` — this `Button` is a Base
               UI primitive with no `asChild`, so nesting one is a type error and
               a nested-anchor bug. Same treatment as the mailbox rail. */
            className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "-mb-2 self-start")}
          >
            <ChevronLeftIcon />
            {t("chat.backToList")}
          </Link>
        ) : null}

        {onGroup && canView ? (
          <GroupRoom />
        ) : peerUserId !== null && canDirect ? (
          /*
           * KEYED ON THE PEER, WHICH IS NOT A DETAIL. Switching person REMOUNTS
           * the thread, so the scroll position, the "have I marked this read" ref
           * inside it and the composer's half-typed body all reset. Without the
           * key, the watermark ref would carry the son's newest id into the CA's
           * conversation and refuse to mark hers read, and a sentence typed for
           * one person would still be in the box addressed to another.
           */
          <DirectThread
            key={peerUserId}
            peerUserId={peerUserId}
            peerName={selected?.peerName ?? null}
          />
        ) : (
          <Card className="flex items-center justify-center p-6" data-testid="no-conversation">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <MessageCircleIcon />
                </EmptyMedia>
                <EmptyTitle>{t("chat.pickTitle")}</EmptyTitle>
                <EmptyDescription>{t("chat.pickHelp")}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          </Card>
        )}
      </div>
    </div>
  )
}

/**
 * `:userId` → a real user id, or nobody.
 *
 * `Number.parseInt` is deliberately not used: it reads "12abc" as 12, and a URL
 * is somebody's typing. Only a string of digits naming a positive integer counts
 * — which is also what makes `group` unambiguous as a sibling pattern, and what
 * makes the retired `/messages/direct` path resolve to nothing.
 */
export function parsePeerId(raw: string | undefined): number | null {
  if (raw === undefined || !/^\d+$/.test(raw)) return null
  const value = Number(raw)
  return Number.isSafeInteger(value) && value > 0 ? value : null
}

/* -------------------------------------------------------------------------- */
/* The one list                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Every conversation this reader has: the people, then the group.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE ORDER IS THE FEATURE, NOT A LAYOUT PREFERENCE. People first because a
 * person is who you came to answer; the group below them because it is one
 * conversation among several rather than the place they all live. A separator
 * between the two says they are different in kind without needing a heading over
 * a list of two names.
 *
 * THE SEPARATOR APPEARS ONLY WHEN BOTH KINDS DO. A rule above the only row on
 * screen is a rule between something and nothing.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * EVERYBODY THE READER MAY WRITE TO APPEARS, INCLUDING THOSE WITH NO HISTORY.
 * That is the server's decision (`GET /messages/direct` doubles as the people
 * directory) and this list depends on it: the only other list of users is behind
 * `user.manage`, which the OWNER does not hold, so without these rows the person
 * who asked for the feature would open it and have nobody to write to. A row with
 * no history says so rather than showing a blank line.
 *
 * EACH ROW SHOWS ITS OWN UNREAD, NEVER A SHARE OF A TOTAL. Read state is per
 * conversation — one watermark per PAIR for a person, one per person for the
 * group — and a single number spread across the rows would be a guess, wrong
 * exactly when two people are waiting.
 *
 * `NavLink` RATHER THAN A BUTTON. Which conversation is open is in the URL, so a
 * row is navigation: middle-click and open-in-new-tab work, and `aria-current`
 * comes from the router rather than from a class this file would have to keep in
 * step with the address bar.
 */
function ConversationList({
  people,
  showPeople,
  showGroup,
  peopleLoading,
  peopleLoaded,
  groupUnread,
  selectedPeerUserId,
}: {
  people: readonly DirectConversationSummary[]
  showPeople: boolean
  showGroup: boolean
  peopleLoading: boolean
  peopleLoaded: boolean
  groupUnread: number
  selectedPeerUserId: number | null
}): React.JSX.Element {
  const { t } = useTranslation()

  return (
    /*
      ═══════════════════════════════════════════════════════════════════════════
      AS TALL AS THE CONVERSATION BESIDE IT, ON THE DESKTOP LAYOUT (owner's ask,
      2026-08-20).

      `md:h-full` RATHER THAN A SECOND `70vh`. Both would look right today and
      only one stays right: the pane's height is its own header plus a `60vh`
      transcript plus a composer, so any number written here is a guess that
      drifts the moment either card changes. `h-full` in a grid item resolves
      against the ROW, and the row is as tall as the tallest child — so this card
      is by construction exactly the pane's height, whatever that becomes.

      `md:max-h-none` IS THE OTHER HALF, and without it nothing changes: the cap
      was BELOW the pane's height (a 60vh transcript plus two chrome bands
      exceeds 70vh on an ordinary laptop), so the card stretched and was then
      clipped short. That clipping is what the owner was looking at.

      THE PHONE KEEPS `max-h-[70vh]`. There the list and the pane are never on
      screen together (`useIsMobile` renders one or the other), so there is no
      height to match — and an uncapped list would push everything below it off
      a phone screen.
      ═══════════════════════════════════════════════════════════════════════════
    */
    <Card
      className="flex max-h-[70vh] flex-col overflow-hidden py-0 md:h-full md:max-h-none"
      data-testid="conversation-list"
    >
      {/*
        THE LIST HAS A NAME NOW. It sat as an unlabelled column of avatars
        beside a card that announces whose conversation is open, so the two
        halves of the screen were not obviously the same kind of thing. It is
        also the accessible name for the region: a screen reader landing in it
        previously had a bare list and no word for what the list was of.
      */}
      <div className="border-b px-3 py-2.5">
        <h2 className="text-sm font-semibold" id="conversation-list-title">
          {t("chat.conversations")}
        </h2>
      </div>

      <ScrollArea className="min-h-0 flex-1" aria-labelledby="conversation-list-title">
        <ul className="flex flex-col p-2">
          {peopleLoading
            ? [0, 1, 2].map((n) => (
                <li key={n} className="flex items-center gap-2 p-2">
                  <Skeleton className="size-8 shrink-0 rounded-full" />
                  <div className="flex flex-1 flex-col gap-1.5">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </li>
              ))
            : null}

          {people.map((row) => (
            <li key={row.peerUserId}>
              <NavLink
                to={routes.messagesWith(row.peerUserId)}
                data-testid="messenger-row"
                data-kind="person"
                data-peer={row.peerUserId}
                className={({ isActive }) =>
                  cn(
                    "hover:bg-muted flex w-full items-center gap-2.5 rounded-md p-2 text-left transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                    isActive || row.peerUserId === selectedPeerUserId ? "bg-muted" : "",
                  )
                }
              >
                <Avatar size="sm" aria-hidden="true">
                  <AvatarFallback>{initialsOf(row.peerName)}</AvatarFallback>
                </Avatar>

                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium">{row.peerName}</span>
                  {/*
                    * THE LAST LINE IS PLAIN TEXT, escaped by React like every
                    * other rendering of a body in this feature. `truncate` is
                    * CSS; nothing parses the message to shorten it.
                    */}
                  <span className="text-muted-foreground truncate text-xs">
                    {row.lastMessage === null ? t("chat.directNothingYet") : row.lastMessage.body}
                  </span>
                </span>

                {row.unread > 0 ? (
                  /*
                   * THE COUNT IS IN THE ACCESSIBLE NAME, not only in the pill — a
                   * screen reader landing on a row would otherwise have no way to
                   * learn that two are waiting in it. `count` also drives
                   * i18next's plural selection, so Marathi gets its own forms.
                   */
                  <span
                    className="bg-primary text-primary-foreground flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] leading-none font-medium tabular-nums"
                    data-testid="row-unread"
                    data-kind="person"
                    data-peer={row.peerUserId}
                    aria-label={t("chat.directUnread", { count: row.unread })}
                  >
                    {row.unread > 99 ? "99+" : row.unread}
                  </span>
                ) : null}
              </NavLink>
            </li>
          ))}

          {/*
            * THE RULE BETWEEN THE PEOPLE AND THE GROUP. Presentational, so it is
            * hidden from the accessibility tree — a screen reader gets the two
            * kinds of row from their own accessible names, not from a divider.
            */}
          {showPeople && showGroup && people.length > 0 ? (
            <li aria-hidden="true" className="px-2 py-1.5" data-testid="messenger-separator">
              <span className="bg-border block h-px w-full" />
            </li>
          ) : null}

          {showGroup ? (
            <li>
              <NavLink
                to={routes.messagesGroup}
                data-testid="messenger-row"
                data-kind="group"
                className={({ isActive }) =>
                  cn(
                    "hover:bg-muted flex w-full items-center gap-2.5 rounded-md p-2 text-left transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                    isActive ? "bg-muted" : "",
                  )
                }
              >
                <Avatar size="sm" aria-hidden="true">
                  <AvatarFallback>
                    <UsersIcon className="size-4" />
                  </AvatarFallback>
                </Avatar>

                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium">{t("chat.groupRow")}</span>
                  <span className="text-muted-foreground truncate text-xs">
                    {t("chat.groupRowHelp")}
                  </span>
                </span>

                {groupUnread > 0 ? (
                  <span
                    className="bg-primary text-primary-foreground flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] leading-none font-medium tabular-nums"
                    data-testid="row-unread"
                    data-kind="group"
                    aria-label={t("chat.groupUnread", { count: groupUnread })}
                  >
                    {groupUnread > 99 ? "99+" : groupUnread}
                  </span>
                ) : null}
              </NavLink>
            </li>
          ) : null}

          {/*
            * "THERE IS NOBODY TO WRITE TO" IS A CLAIM ABOUT THE DIRECTORY, SO IT
            * NEEDS THE DIRECTORY — `peopleLoaded`, never `!peopleLoading`.
            * `isPending` is false in the ERROR state too, so gating on it would
            * put the red "Couldn't load" banner and "nobody to write to" on
            * screen together: the app asserting a fact when the truth is that it
            * is unknown.
            *
            * IT ONLY APPEARS WHEN THERE IS NO GROUP ROW EITHER. A reader holding
            * both capabilities and no colleagues still has a conversation on
            * screen, and telling him the list is empty above a row that is plainly
            * there would be the app contradicting itself.
            */}
          {showPeople && !showGroup && peopleLoaded && people.length === 0 ? (
            <li className="text-muted-foreground p-3 text-sm" data-testid="messenger-nobody">
              {t("chat.directNobody")}
            </li>
          ) : null}
        </ul>
      </ScrollArea>
    </Card>
  )
}
